import { t } from "@lingui/core/macro";
import { ComparatorResult, EMPTY_ARRAY, EMPTY_STRING, stringComparator } from "@regrello/core-utils";
import { DataTestIds } from "@regrello/data-test-ids-api";
import type {
  ConditionOperator,
  CreateFieldInstanceValueInputs,
  FieldAllowedValueString,
  FieldFields,
  FieldInstanceFields,
  FieldInstanceFieldsWithBaseValues,
  FieldInstanceValueInputType,
  PropertyDataType,
  PropertyTypeFields,
  SpectrumFieldVersionFields,
  TableFieldInstanceFields,
  UpdateFieldInstanceValueInputs,
  UpdateStartingConditionsInputs,
  ViewFilterFields,
} from "@regrello/graphql-api";
import { RegrelloIcon } from "@regrello/ui-core";
import type { FieldPath, FieldValues } from "react-hook-form";
import type { FieldInstanceBaseFields } from "../../../../../types";
import { getFieldInstanceId } from "../../../../../utils/customFields/getFieldInstanceId";
import {
  getRegrelloDefaultFilterDefinitionSingleSelectValue,
  getRegrelloFilterDefinitionSingleSelectValue,
} from "../../../../../utils/filters/v2/filterKinds/core/filterSelect";
import { getErrorMessageWithPayload } from "../../../../../utils/getErrorMessageWithPayload";
import { RegrelloInactiveBadge } from "../../../components/inactiveBadge/RegrelloInactiveBadge";
import { RegrelloControlledFormFieldMultiSelect } from "../../../molecules/formFields/controlled/RegrelloControlledFormFieldMultiSelect";
import { RegrelloControlledFormFieldSelect } from "../../../molecules/formFields/controlled/RegrelloControlledFormFieldSelect";
import { RegrelloCustomFieldMultiValuePopover } from "../components/RegrelloCustomFieldMultiValuePopover";
import { CustomFieldPluginRegistrar } from "./registry/customFieldPluginRegistrar";
import type { SelectFieldPluginSingleValuedFrontendValue } from "./types/CustomFieldFrontendSelectableOption";
import {
  type CustomFieldFrontendSelectableOption,
  INACTIVE_SELECTABLE_OPTION_ID,
} from "./types/CustomFieldFrontendSelectableOption";
import type {
  CustomFieldPlugin,
  CustomFieldPluginV2RenderFormFieldProps,
  GetCreateFieldInstanceValueInputsFromFormValueParams,
  RenderDisplayValueOptions,
} from "./types/CustomFieldPlugin";
import { createViewColumnsFromField } from "./utils/createViewColumnsFromField";
import { DEFAULT_INPUT_TYPE_IF_NO_VALUE, getConditionOperatorsByType } from "./utils/customFieldConstants";
import { extractAllowedValuesFromFieldVersionForFrontend } from "./utils/extractAllowedValuesForFrontend";
import { extractAtMostOneTabularArrayOrThrow } from "./utils/extractAtMostOneTabularArrayOrThrow";
import { extractSelectedValueOrThrow } from "./utils/extractSelectedValueOrThrow";
import {
  getIsFieldInstanceFields,
  getIsFieldInstanceValueWithCrossWorkflowFields,
} from "./utils/fieldInstanceTypeguards";
import { getUpdateStartingConditionsInputsForEmptyOperators } from "./utils/getUpdateStartingConditionsInputsForEmptyOperators";
import { extractAllowedValues, normalizeSelectValue, validateRequired } from "./utils/validateBulkUploadCellValueUtils";

// TODO Misc: Turn the following into warnings and handle gracefully so the app doesn't crash in
// case of field misconfiguration.
const ERROR_INVALID_FIELD = "Provided 'select' field is invalid";
const WARNING_INVALID_FORM_VALUE = "Provided 'text'-field form value is not a valid selectable option";
const ERROR_INVALID_VALUE_COUNT = "Provided 'select' field instance cannot have multiple values";
const ERROR_INVALID_VALUE_TYPE = "Provided 'select' field instance value must have type 'FieldInstanceValueString'";
const WARNING_UNEXPECTED_EMPTY_FORM_VALUE =
  "Provided 'select' field form value must not be empty given the provided operator type";
const WARNING_INVALID_OPERATOR_TYPE = "Provided operator type is invalid for 'select' fields";
const WARNING_UNEXPECTED_DEFINED_FORM_VALUE =
  "Provided 'select' field form value must not be defined given the provided operator type";

function canProcessPropertyDataType(propertyDataType: PropertyDataType): boolean {
  return propertyDataType === "STRING";
}

function canProcessField(field: FieldFields): boolean {
  return (
    !field.isMultiValued && field.allowedValues.length > 0 && canProcessPropertyDataType(field.propertyType.dataType)
  );
}

function isItemsEqual(a: CustomFieldFrontendSelectableOption, b: CustomFieldFrontendSelectableOption) {
  return a.id === b.id;
}

// (zstanik): Added `CustomFieldFrontendSelectableOption[]` to the union type here to reflect that a
// `multiselect` input needs to be rendered for certain `select` start condition operator types.
// This won't result in any misconfigured field instances because we still validate that the form
// value is of the correct type before returning any mutation inputs.
type SelectFieldPluginType = CustomFieldPlugin<
  SelectFieldPluginSingleValuedFrontendValue | CustomFieldFrontendSelectableOption[]
>;

const getConditionOperators: SelectFieldPluginType["getConditionOperators"] = () =>
  getConditionOperatorsByType("SELECT");

function renderDisplayValueInternal(
  _fieldVersion: SpectrumFieldVersionFields,
  value: SelectFieldPluginSingleValuedFrontendValue,
  _options?: RenderDisplayValueOptions,
) {
  return (
    <>
      {value?.displayValue}
      {value?.id === INACTIVE_SELECTABLE_OPTION_ID && <RegrelloInactiveBadge />}
    </>
  );
}

const renderDisplayValueFromStringValue: SelectFieldPluginType["renderDisplayValueFromStringValue"] = (
  fieldVersion,
  value,
  options,
) => {
  if (options?.transformToFrontendValue == null) {
    console.warn(
      "No transformToFrontendValue function was provided; this is necessary for this field type." +
        "Returning the value as is.",
    );
    return value;
  }
  const transformedValue = options.transformToFrontendValue(value);
  if (Array.isArray(transformedValue)) {
    console.warn("Multi-values are not supported for this field type. Returning the value as is.");
    return value;
  }
  return renderDisplayValueInternal(fieldVersion, transformedValue, options);
};

const renderDisplayValue: SelectFieldPluginType["renderDisplayValue"] = (fieldInstance) => {
  const frontendFieldInstance = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance, {
    includeInactiveOptions: true,
  });

  const frontendValue = frontendFieldInstance.value;

  return (
    <>
      {frontendValue?.displayValue}
      {frontendValue?.id === INACTIVE_SELECTABLE_OPTION_ID && <RegrelloInactiveBadge />}
    </>
  );
};

const sortComparator: SelectFieldPluginType["sortComparator"] = (
  fieldInstance1,
  fieldInstance2,

  direction = "asc",
): ComparatorResult => {
  if (direction === "desc") {
    return SelectFieldPlugin.sortComparator(fieldInstance2, fieldInstance1, "asc");
  }

  if (fieldInstance1 == null) {
    return ComparatorResult.BEFORE;
  }

  if (fieldInstance2 == null) {
    return ComparatorResult.AFTER;
  }

  const fieldPlugin = CustomFieldPluginRegistrar.getPluginForField(fieldInstance1.field);

  const value1 = String(fieldPlugin.renderDisplayValue(fieldInstance1));
  const value2 = String(fieldPlugin.renderDisplayValue(fieldInstance2));

  return stringComparator(value1, value2);
};

/**
 * Describes a custom field that allows the user to select one value from an approved list.
 */
export const SelectFieldPlugin: SelectFieldPluginType = {
  uri: "com.regrello.customField.select",
  version: "1.0.0",

  canProcessField: (field: FieldFields): boolean => {
    return canProcessField(field);
  },

  canProcessFieldInstance: (fieldInstance: FieldInstanceBaseFields): boolean => {
    try {
      translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance);
      return true;
    } catch (_error) {
      return false;
    }
  },

  canProcessPropertyDataType,

  findPropertyTypeIdFromLoadedPropertyTypeIds: (loadedPropertyTypes: PropertyTypeFields[]): number | undefined => {
    // (clewis): 'Select' fields always hold text/string values.
    return loadedPropertyTypes.find((propertyType) => propertyType.dataType === "STRING")?.id;
  },

  getColumnsForTable: createViewColumnsFromField,

  getConditionOperators,

  getCreateFieldInstanceValueInputsFromFormValue: (
    inputs: GetCreateFieldInstanceValueInputsFromFormValueParams,
  ): CreateFieldInstanceValueInputs => {
    const { displayOrder, field, inputType, displayContext, sourceFieldInstanceId, spectrumFieldVersion, value } =
      inputs;
    if (!isValueValidAndSingleValued(value)) {
      throw new Error(getErrorMessageWithPayload(WARNING_INVALID_FORM_VALUE, { field, inputType, value }));
    }

    const allowedValues = extractAllowedValuesFromFieldVersionForFrontend(spectrumFieldVersion);

    if (
      value != null &&
      !allowedValues.some((allowedValue) => allowedValue.id === value.id && allowedValue.value === value.value)
    ) {
      console.warn(
        "[SelectFieldPlugin] Received a value that does not appear in allowedValues. Silently ignoring it and using an empty value instead.",
        { field, value: value },
      );
      return {
        fieldId: field.id,
        inputType,
        displayContext: displayContext ?? "DEFAULT",
        displayOrder,
        spectrumFieldVersionId: spectrumFieldVersion.id,
        sourceFieldInstanceValueId: sourceFieldInstanceId,
      };
    }
    return {
      fieldId: field.id,
      stringValue: value?.value ?? undefined,
      inputType,
      displayContext: displayContext ?? "DEFAULT",
      displayOrder,
      spectrumFieldVersionId: spectrumFieldVersion.id,
      sourceFieldInstanceValueId: sourceFieldInstanceId,
    };
  },

  getCrossWorkflowSinksFieldInstanceIds: (
    fieldInstance: FieldInstanceFields | FieldInstanceFieldsWithBaseValues,
  ): number[] => {
    const frontendFieldInstance = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance);
    return frontendFieldInstance.crossWorkflowSinksFieldInstanceIds;
  },

  getCrossWorkflowSourceFieldInstanceIdFromValue: (
    fieldInstance: FieldInstanceFields | FieldInstanceFieldsWithBaseValues,
  ): number | undefined => {
    const frontendFieldInstance = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance);
    return frontendFieldInstance.crossWorkflowSourceFieldInstanceId;
  },

  getFieldDisplayName: (): string => {
    return t`Select`;
  },

  getFilterDefinition: (field: FieldFields) => {
    return getRegrelloDefaultFilterDefinitionSingleSelectValue({
      type: "default",
      values: field.allowedValues.filter(isFieldAllowedValueString).map((value) => ({
        id: value.id,
        label: value.displayStringValue,
        value: value.stringValue,
      })),
    });
  },

  getFilterDefinitionWithValues: (field: FieldFields, filter: ViewFilterFields) => {
    const values = filter.values ?? EMPTY_ARRAY;
    const allowedValues = field.allowedValues.filter(isFieldAllowedValueString).map((value) => ({
      id: value.id,
      label: value.displayStringValue,
      value: value.stringValue,
    }));
    return getRegrelloFilterDefinitionSingleSelectValue(filter.operator, values, allowedValues);
  },

  getEmptyValueForFrontend: (
    options:
      | {
          operator: ConditionOperator;
        }
      | undefined,
  ): CustomFieldFrontendSelectableOption | CustomFieldFrontendSelectableOption[] | null => {
    if (options != null) {
      if (!isOperatorValid(options.operator)) {
        console.warn(WARNING_INVALID_OPERATOR_TYPE, {
          operator: options.operator,
        });
        return null;
      }
      if (options.operator === "CONTAINS_ANY_OF" || options.operator === "CONTAINS_NONE_OF") {
        return EMPTY_ARRAY;
      }
    }
    return null;
  },

  getEmptyTabularValueForFrontend: (
    rowNumber: number,
  ): { value: CustomFieldFrontendSelectableOption | null; rowNumber: number } => {
    return { value: null, rowNumber };
  },

  getHelperText: (): string => {
    return t`Select allows you to select a single option from predefined options in a dropdown.`;
  },

  getIconName: () => {
    return "select-field";
  },

  getNameTemplateDisplayValueFromFormValue: (value) => {
    if (!isCustomFieldSelectableOption(value) || value == null || value.value === EMPTY_STRING) {
      return undefined;
    }
    return value.displayValue;
  },

  getSourceFieldInstance: (_fieldInstance: FieldInstanceFields): FieldInstanceFields | undefined => {
    return undefined;
  },

  getSourceFieldInstanceId: (fieldInstance: FieldInstanceBaseFields): number | undefined => {
    const frontendFieldInstance = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance);
    return frontendFieldInstance.sourceFieldInstanceId;
  },

  getSourceFieldInstanceInputType: (
    fieldInstance: FieldInstanceFields | FieldInstanceBaseFields,
  ): FieldInstanceValueInputType | undefined => {
    const sourceValue = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance);
    return sourceValue.sourceFieldInstanceInputType;
  },

  getSinkFieldInstanceIds: (fieldInstance: FieldInstanceFields | FieldInstanceBaseFields) => {
    const frontendFieldInstance = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance);
    return frontendFieldInstance.sinksFieldInstanceIds;
  },

  getUpdateStartingConditionsInputsFromFormValues: (
    leftFieldInstance: FieldInstanceFields,
    value: unknown,
    operator: ConditionOperator,
  ): UpdateStartingConditionsInputs | undefined => {
    if (!isOperatorValid(operator)) {
      console.warn(WARNING_INVALID_OPERATOR_TYPE, {
        leftFieldInstance,
        operator,
      });
      return undefined;
    }

    if (operator === "EMPTY" || operator === "NOT_EMPTY") {
      if (value != null) {
        console.warn(WARNING_UNEXPECTED_DEFINED_FORM_VALUE, {
          leftFieldInstance,
          value,
        });
      }
      return getUpdateStartingConditionsInputsForEmptyOperators(getFieldInstanceId(leftFieldInstance), operator);
    }

    // (zstanik): For "Is in" and "Is not in", represented by CONTAINS_ANY and CONTAINS_NONE
    // respectively, the right side of the conditional expression is treated as `stringMultiValue`.
    if (operator === "CONTAINS_ANY_OF" || operator === "CONTAINS_NONE_OF") {
      if (value == null || (Array.isArray(value) && value.length === 0)) {
        console.warn(WARNING_UNEXPECTED_EMPTY_FORM_VALUE, {
          leftFieldInstance,
          value,
        });
        return undefined;
      }
      if (!isValueValidAndMultiValued(value)) {
        console.warn(WARNING_INVALID_FORM_VALUE, { leftFieldInstance, value });
        return undefined;
      }

      const allowedValues = extractAllowedValuesFromFieldVersionForFrontend(leftFieldInstance.spectrumFieldVersion);

      // (clewis): O(N^2) but this method will be called rarely, so no big deal.
      const validOptions = value.filter((selectedOption) =>
        allowedValues.some(
          (allowedValue) => allowedValue.id === selectedOption.id && allowedValue.displayValue === selectedOption.value,
        ),
      );
      if (validOptions.length !== value.length) {
        console.warn(
          "[MultiSelectFieldPlugin] Received one or more values that do not appear in allowedValues. Silently ignoring them.",
          { leftFieldInstance, value: value, validOptions },
        );
      }

      return {
        leftFieldInstanceValueID: getFieldInstanceId(leftFieldInstance),
        operatorV2: operator,
        rightFloatMultiValue: EMPTY_ARRAY,
        rightIntMultiValue: EMPTY_ARRAY,
        rightPartyIDMultiValue: EMPTY_ARRAY,
        rightStringMultiValue: validOptions.map((option) => option.value),
        rightTimeMultiValue: EMPTY_ARRAY,
        rightDateMultiValue: EMPTY_ARRAY,
      };
    }

    // (zstanik): For all other operators, the value is expected to be a single `CustomFieldFrontendSelectableOption`
    if (value == null) {
      console.warn(WARNING_UNEXPECTED_EMPTY_FORM_VALUE, {
        leftFieldInstance,
        value,
      });
      return undefined;
    }
    if (!isValueValidAndSingleValued(value)) {
      console.warn(WARNING_INVALID_FORM_VALUE, { leftFieldInstance, value });
      return undefined;
    }

    return {
      leftFieldInstanceValueID: getFieldInstanceId(leftFieldInstance),
      operatorV2: operator,
      rightStringValue: value.value,
      rightFloatMultiValue: EMPTY_ARRAY,
      rightIntMultiValue: EMPTY_ARRAY,
      rightPartyIDMultiValue: EMPTY_ARRAY,
      rightStringMultiValue: EMPTY_ARRAY,
      rightTimeMultiValue: EMPTY_ARRAY,
      rightDateMultiValue: EMPTY_ARRAY,
    };
  },
  // (hchen): Currently this is only used for handling the edge case when flipping the field
  // instance input type from OPTIONAL to REQUESTED (i.e. required) when the a previously OPTIONAL
  // field is set as a native field on a future task. It's not necessary to implement for field
  // types other than date and user, since we only support those as native field now.
  getUpdateFieldInstanceValueInputsFromFieldInstance: (
    _fieldInstance: FieldInstanceFields | FieldInstanceFieldsWithBaseValues,
  ): UpdateFieldInstanceValueInputs[] => {
    throw new Error("not implmented yet");
  },

  getValueForFrontend: (
    fieldInstance: FieldInstanceFields | FieldInstanceBaseFields,
    options?: { includeInactiveValues: boolean },
  ): CustomFieldFrontendSelectableOption | null => {
    return translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance, {
      includeInactiveOptions: options?.includeInactiveValues ?? false,
    }).value;
  },

  getTabularValueForFrontend: (fieldInstance: TableFieldInstanceFields) => {
    return translateGraphQlFieldInstanceToTabularFrontendFieldInstanceOrThrow(fieldInstance);
  },

  hasAllowedValues: (): boolean => {
    return true;
  },

  isCreateAndEditAllowed: true,

  isFeatureFlagEnabled: (): boolean => {
    return true;
  },

  isFieldInstanceEmpty: (
    fieldInstance: FieldInstanceFields | FieldInstanceFieldsWithBaseValues,
    options?: { includeInactiveOptions: boolean },
  ): boolean => {
    const frontendFieldInstance = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance, {
      includeInactiveOptions: options?.includeInactiveOptions ?? false,
    });
    return frontendFieldInstance.value == null;
  },

  isFieldInstanceValueEqual: (
    fieldInstance: FieldInstanceFields | FieldInstanceFieldsWithBaseValues,
    fieldInstance2: FieldInstanceFields | FieldInstanceFieldsWithBaseValues,
  ): boolean => {
    const frontendFieldInstance = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance);
    const frontendFieldInstance2 = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance2);
    return frontendFieldInstance.value?.id === frontendFieldInstance2.value?.id;
  },

  isFieldInstanceValueUnchanged: (fieldInstance, proposedChange): boolean => {
    const frontendFieldInstance = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance);
    if (frontendFieldInstance.sourceFieldInstanceId !== proposedChange.sourceFieldInstanceValueId) {
      return false;
    }

    if (frontendFieldInstance.inputType !== proposedChange.inputType) {
      return false;
    }

    return (
      (frontendFieldInstance.value == null && proposedChange.stringValue == null) ||
      frontendFieldInstance.value?.displayValue === proposedChange.stringValue // compare translated value with the raw user input value
    );
  },

  isFieldInstanceTabularValueUnchanged: (fieldInstance, proposedChange): boolean => {
    const rows = translateGraphQlFieldInstanceToTabularFrontendFieldInstanceOrThrow(fieldInstance);

    const proposedRow = rows.find((row) => row.rowNumber === proposedChange.rowNumber);

    // Changed if the proposed row is a new row:
    if (proposedRow == null) {
      return false;
    }

    // Unchanged if both are empty values:
    if (proposedRow.value == null) {
      return proposedChange.stringValue == null;
    }

    // Unchanged if both values are defined and equal:
    // compare translated value with the raw user input value
    if (proposedRow.value.displayValue === proposedChange.stringValue) {
      return true;
    }

    // Changed!
    return false;
  },

  renderDisplayValue: renderDisplayValue,

  renderDisplayValueFromStringValue: renderDisplayValueFromStringValue,

  renderFormField: <TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>(
    field: FieldFields,
    props: CustomFieldPluginV2RenderFormFieldProps<TFieldValues, TName>,
  ): React.ReactNode => {
    if (!canProcessField(field)) {
      throw new Error(getErrorMessageWithPayload(ERROR_INVALID_FIELD, { field }));
    }
    const { operator, fieldInstance } = props;
    if (fieldInstance == null) {
      throw new Error("Field instance was not defined. Cannot render form field without field version information.");
    }

    const isDeleted = fieldInstance?.field.deletedAt != null;
    if (operator != null) {
      if (!isOperatorValid(operator)) {
        console.warn(WARNING_INVALID_OPERATOR_TYPE, {
          operator,
        });
        return undefined;
      }

      // (zstanik): For "Is in" and "Is not in", represented by CONTAINS_ANY and CONTAINS_NONE
      // respectively, the right side of the conditional expression is treated as
      // `stringMultiValue`, thus a MultiSelect input needs to be rendered.
      if (operator === "CONTAINS_ANY_OF" || operator === "CONTAINS_NONE_OF") {
        return (
          <RegrelloControlledFormFieldMultiSelect
            {...props}
            key={`${props.controllerProps.name}-${props.keySuffix}`}
            dataTestId={DataTestIds.CUSTOM_FIELD_VALUE_INPUT}
            getSelectedItemProps={getSelectedItemProps}
            infoTooltipText={props.description}
            isDeleted={isDeleted}
            isItemsEqual={isItemsEqual}
            itemPredicate={itemPredicate}
            items={extractAllowedValuesFromFieldVersionForFrontend(fieldInstance.spectrumFieldVersion)}
            renderItem={renderItem}
          />
        );
      }
    }

    return (
      <RegrelloControlledFormFieldSelect
        {...props}
        key={`${props.controllerProps.name}-${props.keySuffix}`}
        dataTestId={DataTestIds.CUSTOM_FIELD_VALUE_INPUT}
        getOptionLabel={getOptionLabel}
        infoTooltipText={props.description}
        isClearButtonEnabled={true}
        isDeleted={isDeleted}
        options={extractAllowedValuesFromFieldVersionForFrontend(fieldInstance.spectrumFieldVersion)}
        renderSelectedValue={renderInactiveSelectedValue}
      />
    );
  },

  renderIcon: (props) => {
    return <RegrelloIcon {...props} iconName="select-field" />;
  },

  renderMultipleDisplayValuesForDataGrid: (fieldInstances, options) => {
    if (fieldInstances.length === 0) {
      return null;
    }
    if (fieldInstances.length === 1) {
      return renderDisplayValue(fieldInstances[0], { context: options?.context ?? "table" });
    }

    const instancesWithSource = fieldInstances.map((fieldInstance) => {
      return {
        content: renderDisplayValue(fieldInstance, { context: options?.context ?? "table" }),
        workflowName: fieldInstance.workflow?.name,
        stageName: fieldInstance.actionItem?.workflowReference?.stageName,
        taskName: fieldInstance.actionItem?.name,
      };
    });

    return <RegrelloCustomFieldMultiValuePopover instancesWithSource={instancesWithSource} />;
  },

  sortComparator,

  validateBulkUploadCellValue: (field, value, options) => {
    const valueAsString = value.trim();

    if (options.isRequired) {
      const result = validateRequired(valueAsString, options.isRequired);
      if (result != null) {
        return result;
      }
    }

    if (valueAsString.length === 0) {
      return {
        value: valueAsString,
      };
    }

    const untranslatedAllowedValueStrings = extractAllowedValues(field, options.fieldVersion);
    const allowedValuesByNormalizedValue = new Map(
      untranslatedAllowedValueStrings.map((valueInternal) => [normalizeSelectValue(valueInternal), valueInternal]),
    );

    if (!allowedValuesByNormalizedValue.has(normalizeSelectValue(valueAsString))) {
      return {
        value: valueAsString,
        info: [
          {
            message: t`Must be a recognized value`,
            level: "error",
          },
        ],
      };
    }

    return {
      value: allowedValuesByNormalizedValue.get(valueAsString.toLocaleLowerCase()),
    };
  },
};

interface FrontendSelectFieldInstance {
  allowedValues: CustomFieldFrontendSelectableOption[];
  crossWorkflowSinksFieldInstanceIds: number[];
  crossWorkflowSourceFieldInstanceId: number | undefined;
  inputType: FieldInstanceValueInputType;
  name: string;
  sinksFieldInstanceIds: number[];
  sourceFieldInstanceId: number | undefined;
  sourceFieldInstanceInputType: FieldInstanceValueInputType | undefined;
  value: CustomFieldFrontendSelectableOption | null;
}

function getOptionLabel(allowedValue: CustomFieldFrontendSelectableOption) {
  return allowedValue.displayValue;
}

function getSelectedItemProps(allowedValue: CustomFieldFrontendSelectableOption) {
  return {
    children: renderInactiveSelectedValue(allowedValue),
  };
}

function renderInactiveSelectedValue(allowedValue: CustomFieldFrontendSelectableOption | null) {
  return <InternalSelectedItem allowedValue={allowedValue} />;
}

/**
 * Renders a selected item that might be inactive (meaning it is no longer
 * included in the allowed values as of this field version).
 */
// eslint-disable-next-line react-refresh/only-export-components
function InternalSelectedItem({ allowedValue }: { allowedValue: CustomFieldFrontendSelectableOption | null }) {
  if (allowedValue == null) {
    return null;
  }

  if (allowedValue.id === INACTIVE_SELECTABLE_OPTION_ID) {
    return (
      <div>
        {allowedValue.displayValue}
        <span className="text-textMuted"> {t`(Inactive)`}</span>
      </div>
    );
  }

  return <div>{allowedValue.displayValue}</div>;
}

function itemPredicate(query: string, allowedValue: CustomFieldFrontendSelectableOption) {
  return allowedValue.displayValue.toLocaleLowerCase().includes(query.toLocaleLowerCase());
}

function renderItem(allowedValue: CustomFieldFrontendSelectableOption) {
  return {
    key: allowedValue.id,
    text: allowedValue.displayValue,
  };
}

function translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(
  fieldInstance: FieldInstanceFields | FieldInstanceBaseFields,
  options?: {
    includeInactiveOptions: boolean;
  },
): FrontendSelectFieldInstance {
  const { field } = fieldInstance;

  if (!canProcessField(field)) {
    throw new Error(getErrorMessageWithPayload(ERROR_INVALID_FIELD, { field }));
  }

  const isFieldInstanceFields = getIsFieldInstanceFields(fieldInstance);

  return extractSelectedValueOrThrow<FrontendSelectFieldInstance>({
    fieldInstance,
    errorMessageIfMultipleValues: ERROR_INVALID_VALUE_COUNT,
    errorMessageIfWrongValueType: ERROR_INVALID_VALUE_TYPE,
    getterIfNoValue: (allowedValues) => ({
      name: field.name,
      allowedValues,
      inputType: DEFAULT_INPUT_TYPE_IF_NO_VALUE,
      crossWorkflowSinksFieldInstanceIds: EMPTY_ARRAY,
      crossWorkflowSourceFieldInstanceId: undefined,
      sinksFieldInstanceIds: EMPTY_ARRAY,
      sourceFieldInstanceId: undefined,
      sourceFieldInstanceInputType: undefined,
      value: null,
    }),
    getterIfValue: (fieldInstanceValues, frontendSelectedOptions, allowedValues) => ({
      name: field.name,
      allowedValues,
      inputType: fieldInstanceValues[0].inputType,
      crossWorkflowSinksFieldInstanceIds:
        getIsFieldInstanceValueWithCrossWorkflowFields(fieldInstanceValues[0]) &&
        fieldInstanceValues[0].crossWorkflowSinksFieldInstanceValueString != null
          ? fieldInstanceValues[0].crossWorkflowSinksFieldInstanceValueString.map((value) => value.id)
          : EMPTY_ARRAY,
      crossWorkflowSourceFieldInstanceId: getIsFieldInstanceValueWithCrossWorkflowFields(fieldInstanceValues[0])
        ? fieldInstanceValues[0].crossWorkflowSourceFieldInstanceValueString?.id
        : undefined,
      sinksFieldInstanceIds: isFieldInstanceFields
        ? fieldInstanceValues[0].sinksFieldInstanceValueString?.map(({ id }) => id)
        : EMPTY_ARRAY,
      sourceFieldInstanceId: isFieldInstanceFields
        ? (fieldInstanceValues[0].sourceFieldInstanceValueString?.id ?? undefined)
        : undefined,
      sourceFieldInstanceInputType: isFieldInstanceFields
        ? fieldInstanceValues[0].sourceFieldInstanceValueString?.inputType
        : undefined,
      value: frontendSelectedOptions.length === 0 ? null : frontendSelectedOptions[0],
    }),
    options: {
      includeInactiveOptions: options?.includeInactiveOptions ?? false,
    },
  });
}

function isOperatorValid(
  operator: ConditionOperator,
): operator is "EMPTY" | "NOT_EMPTY" | "EQUALS" | "NOT_EQUALS" | "CONTAINS_ANY_OF" | "CONTAINS_NONE_OF" {
  return getConditionOperators().find((stageStartOperator) => stageStartOperator.operator === operator) != null;
}

function isFieldAllowedValueString(
  value: FieldFields["allowedValues"][0],
): value is FieldAllowedValueString & { __typename: "FieldAllowedValueString" } {
  return value.__typename === "FieldAllowedValueString";
}

function isCustomFieldSelectableOption(value: unknown): value is CustomFieldFrontendSelectableOption {
  return (
    value != null &&
    typeof value === "object" &&
    "id" in value &&
    "value" in value &&
    "displayValue" in value &&
    typeof value.id === "number" &&
    typeof value.value === "string" &&
    typeof value.displayValue === "string"
  );
}

function isValueValidAndSingleValued(value: unknown): value is { id: number; value: string } | null {
  return (
    value == null ||
    (typeof value === "object" &&
      "id" in value &&
      "value" in value &&
      typeof value.id === "number" &&
      typeof value.value === "string")
  );
}

function isValueValidAndMultiValued(value: unknown): value is Array<{ id: number; value: string }> {
  return (
    Array.isArray(value) &&
    value.every(
      // this still returns true if value is an empty array.
      (element) => element != null && isValueValidAndSingleValued(element),
    )
  );
}

function translateGraphQlFieldInstanceToTabularFrontendFieldInstanceOrThrow(
  fieldInstance: TableFieldInstanceFields,
): Array<{ rowNumber: number; value: CustomFieldFrontendSelectableOption | null }> {
  const spectrumFieldVersion = fieldInstance.spectrumFieldVersion;
  if (spectrumFieldVersion == null) {
    return EMPTY_ARRAY;
  }
  const allowedValues = extractAllowedValuesFromFieldVersionForFrontend(spectrumFieldVersion);
  const allowedValuesByValue = new Map(allowedValues.map((value) => [value.value, value]));
  return extractAtMostOneTabularArrayOrThrow({
    fieldInstance,

    fieldInstanceValueTypeName: "FieldInstanceValueString",
    errorMessageIfMultipleValues: ERROR_INVALID_VALUE_COUNT,
    errorMessageIfWrongValueType: ERROR_INVALID_VALUE_TYPE,
    getterIfNoValue: () => EMPTY_ARRAY,
    getterIfTabularValue: (fieldInstanceValue) =>
      fieldInstanceValue.tabularValueString?.map((value) => {
        if (value.value == null) {
          return {
            rowNumber: value.rowNumber,
            value: null,
          };
        }

        const allowedValue = allowedValuesByValue.get(value.value);
        if (allowedValue == null) {
          return {
            rowNumber: value.rowNumber,
            value: null,
          };
        }
        return {
          rowNumber: value.rowNumber,
          value: {
            displayValue: allowedValue.displayValue,
            id: allowedValue.id,
            value: allowedValue.value,
          },
        };
      }) ?? EMPTY_ARRAY,
  });
}
